// https://www.radix-ui.com/primitives/docs/components/tooltip
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";
import cx from "ui/cx";
import { usePopoverContainer } from "ui/hooks/usePopoverContainer";
import styles from "./Tooltip.module.scss";

interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
  disableHoverableContent?: boolean;
}

interface TooltipRootProps {
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
  disableHoverableContent?: boolean;
}

interface TooltipTriggerProps {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}

interface TooltipContentProps {
  children: ReactNode;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  alignOffset?: number;
  avoidCollisions?: boolean;
  collisionBoundary?: Element | null | Array<Element | null>;
  collisionPadding?: number | Partial<Record<"top" | "right" | "bottom" | "left", number>>;
  arrowPadding?: number;
  sticky?: "partial" | "always";
  hideWhenDetached?: boolean;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  forceMount?: true;
  ariaLabel?: string;
  // Portal props
  container?: HTMLElement;
  // Arrow props
  showArrow?: boolean;
  arrowClassName?: string;
  arrowWidth?: number;
  arrowHeight?: number;
  arrowAsChild?: boolean;
}

interface TooltipArrowProps {
  className?: string;
  width?: number;
  height?: number;
  asChild?: boolean;
}

interface TooltipPortalProps {
  children: ReactNode;
  forceMount?: true;
  container?: HTMLElement;
}

function TooltipProvider({ children, delayDuration = 700, skipDelayDuration = 300, ...props }: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration} {...props}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

function TooltipRoot({ children, ...props }: TooltipRootProps) {
  return <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>;
}

function TooltipTrigger({ children, className, ...props }: TooltipTriggerProps) {
  return (
    <TooltipPrimitive.Trigger className={className} data-testid="tooltip-trigger" data-tooltip-trigger {...props}>
      {children}
    </TooltipPrimitive.Trigger>
  );
}

function TooltipPortal({ children, container, ...props }: TooltipPortalProps) {
  return (
    <TooltipPrimitive.Portal container={container} {...props}>
      {children}
    </TooltipPrimitive.Portal>
  );
}

function TooltipContent({
  children,
  className,
  side = "top",
  sideOffset = 3,
  align = "center",
  collisionPadding = 12,
  hideWhenDetached = false,
  // Portal props
  container,
  forceMount,
  // Arrow props
  showArrow = true,
  arrowClassName,
  arrowWidth = 10,
  arrowHeight = 5,
  arrowAsChild = false,
  ariaLabel,
  ...props
}: TooltipContentProps) {
  const popoverContainer = usePopoverContainer();
  const tooltipContainer = container ?? popoverContainer;

  return (
    <TooltipPrimitive.Portal container={tooltipContainer} forceMount={forceMount}>
      <TooltipPrimitive.Content
        className={cx(styles.content, className)}
        side={side}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        hideWhenDetached={hideWhenDetached}
        aria-label={ariaLabel}
        data-testid="tooltip-content"
        {...props}
      >
        {children}
        {showArrow && (
          <TooltipPrimitive.Arrow
            className={cx(styles.arrow, arrowClassName)}
            width={arrowWidth}
            height={arrowHeight}
            asChild={arrowAsChild}
            data-testid="tooltip-arrow"
          />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

function TooltipArrow({ className, width = 10, height = 5, ...props }: TooltipArrowProps) {
  return (
    <TooltipPrimitive.Arrow
      className={cx(styles.arrow, className)}
      width={width}
      height={height}
      data-testid="tooltip-arrow"
      {...props}
    />
  );
}

// Compound component exports
export const Tooltip = {
  Provider: TooltipProvider,
  Root: TooltipRoot,
  Trigger: TooltipTrigger,
  Portal: TooltipPortal,
  Content: TooltipContent,
  Arrow: TooltipArrow,
};

// Type exports
export type {
  TooltipProviderProps,
  TooltipRootProps,
  TooltipTriggerProps,
  TooltipContentProps,
  TooltipArrowProps,
  TooltipPortalProps,
};

export default Tooltip;
